import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const Hero = () => {
  const [showAnimations, setShowAnimations] = useState(false);

  // Defer non-critical animations until after LCP
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => setShowAnimations(true));
    } else {
      // Fallback for Safari
      setTimeout(() => setShowAnimations(true), 100);
    }
  }, []);

  const benefits = [
    "Superior grip that lasts hours",
    "No messy chalk dust everywhere",
    "Perfect for climbing, lifting & gymnastics",
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Hero Background Image - LCP element with explicit dimensions */}
      <div className="absolute inset-0 hero-bg">
        <img
          src="/images/hero-chalk-grip.webp"
          alt="Athlete's hands covered in liquid chalk gripping a barbell"
          className="w-full h-full object-cover"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="sync"
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/90 via-charcoal/70 to-charcoal/50" />
      </div>
      
      {/* Chalk texture overlay */}
      <div className="absolute inset-0 chalk-texture pointer-events-none" />
      
      {/* Animated background elements - deferred until after LCP */}
      {showAnimations && (
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{ y: [0, -20, 0], opacity: [0.1, 0.2, 0.1] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ y: [0, 20, 0], opacity: [0.1, 0.15, 0.1] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl"
          />
        </div>
      )}

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge - CSS animation instead of Framer Motion for initial render */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-primary text-sm font-medium">#1 Choice for Athletes</span>
          </div>

          {/* Main Headline - CSS animation */}
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-secondary-foreground leading-tight mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
            Unlock Your
            <span className="block text-gradient">Maximum Grip</span>
          </h1>

          {/* Subheadline - CSS animation */}
          <p className="text-lg md:text-xl text-muted max-w-2xl mx-auto mb-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            Liquid chalk delivers 3x longer-lasting grip than traditional chalk, 
            without the mess. Professional athletes trust it. Now you can too.
          </p>

          {/* Benefits - CSS animation */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 mb-10 animate-fade-in" style={{ animationDelay: '0.3s' }}>
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-2 text-muted">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-sm md:text-base">{benefit}</span>
              </div>
            ))}
          </div>

          {/* CTAs - CSS animation */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <a
              href="https://amzn.to/49KcFQm"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-cta group"
            >
              Get Liquid Chalk Now
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#quiz"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-secondary-foreground rounded-xl border-2 border-secondary-foreground/20 hover:bg-secondary-foreground/5 transition-all"
            >
              Take the Grip Quiz
            </a>
          </div>

          {/* Social Proof - CSS animation */}
          <div className="mt-16 pt-8 border-t border-secondary-foreground/10 animate-fade-in" style={{ animationDelay: '0.6s' }}>
            <p className="text-muted text-sm mb-4">Trusted by athletes worldwide</p>
            <div className="flex items-center justify-center gap-8 md:gap-12">
              <div className="text-center">
                <p className="font-display text-3xl font-bold text-secondary-foreground">50K+</p>
                <p className="text-muted text-xs">Happy Athletes</p>
              </div>
              <div className="text-center">
                <p className="font-display text-3xl font-bold text-secondary-foreground">4.9★</p>
                <p className="text-muted text-xs">Average Rating</p>
              </div>
              <div className="text-center">
                <p className="font-display text-3xl font-bold text-secondary-foreground">3x</p>
                <p className="text-muted text-xs">Longer Grip</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator - deferred */}
      {showAnimations && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ opacity: { delay: 1 }, y: { duration: 2, repeat: Infinity } }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 rounded-full border-2 border-secondary-foreground/30 flex justify-center pt-2">
            <div className="w-1.5 h-3 rounded-full bg-primary" />
          </div>
        </motion.div>
      )}
    </section>
  );
};

export default Hero;
